import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { RiskAssessmentListResponse } from '@/types/risk.types';
import { RiskLevel, RiskLevelConfig } from '@/types/risk.types';

interface RiskHistoryChartProps {
  history: RiskAssessmentListResponse[];
  onSelectAssessment?: (id: string) => void;
}

export function RiskHistoryChart({ history, onSelectAssessment }: RiskHistoryChartProps) {
  if (history.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center rounded-lg border bg-muted/20">
        <p className="text-muted-foreground">Nenhum histórico de avaliação</p>
      </div>
    );
  }

  // Sort by date ascending for chart
  const sortedHistory = [...history].sort(
    (a, b) => new Date(a.assessedAt).getTime() - new Date(b.assessedAt).getTime()
  );

  const getRiskLevelValue = (level: RiskLevel): number => {
    switch (level) {
      case RiskLevel.LOW:
        return 1;
      case RiskLevel.MODERATE:
        return 2;
      case RiskLevel.HIGH:
        return 3;
      case RiskLevel.CRITICAL:
        return 4;
      default:
        return 0;
    }
  };

  const maxPoints = Math.min(sortedHistory.length, 10);
  const displayHistory = sortedHistory.slice(-maxPoints);

  // Calculate trend
  const getTrend = () => {
    if (displayHistory.length < 2) return 'stable';
    const lastItem = displayHistory[displayHistory.length - 1];
    const prevItem = displayHistory[displayHistory.length - 2];
    if (!lastItem || !prevItem) return 'stable';
    const lastValue = getRiskLevelValue(lastItem.overallLevel);
    const prevValue = getRiskLevelValue(prevItem.overallLevel);
    if (lastValue > prevValue) return 'up';
    if (lastValue < prevValue) return 'down';
    return 'stable';
  };

  const trend = getTrend();

  return (
    <div className="space-y-4">
      {/* Trend Summary */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Tendência:</span>
        {trend === 'up' && (
          <span className="flex items-center gap-1 text-red-600">
            <TrendingUp className="h-4 w-4" />
            Aumento do risco
          </span>
        )}
        {trend === 'down' && (
          <span className="flex items-center gap-1 text-green-600">
            <TrendingDown className="h-4 w-4" />
            Redução do risco
          </span>
        )}
        {trend === 'stable' && (
          <span className="flex items-center gap-1 text-gray-600">
            <Minus className="h-4 w-4" />
            Estável
          </span>
        )}
      </div>

      {/* Simple Visual Chart */}
      <div className="rounded-lg border bg-card p-4">
        <div className="relative h-32">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 flex h-full flex-col justify-between text-xs text-muted-foreground">
            <span>Crítico</span>
            <span>Alto</span>
            <span>Mod.</span>
            <span>Baixo</span>
          </div>

          {/* Chart area */}
          <div className="ml-14 h-full">
            {/* Grid lines */}
            <div className="absolute inset-y-0 right-0 left-14 flex flex-col justify-between">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="border-t border-dashed border-gray-200" />
              ))}
            </div>

            {/* Data points */}
            <div className="relative flex h-full items-end justify-between gap-2">
              {displayHistory.map((assessment) => {
                const value = getRiskLevelValue(assessment.overallLevel);
                const height = (value / 4) * 100;
                const config = RiskLevelConfig[assessment.overallLevel];

                return (
                  <div
                    key={assessment.id}
                    className="group relative flex flex-1 flex-col items-center"
                  >
                    {/* Bar */}
                    <button
                      onClick={() => onSelectAssessment?.(assessment.id)}
                      className="w-full max-w-8 cursor-pointer rounded-t transition hover:opacity-80"
                      style={{
                        height: `${height}%`,
                        backgroundColor: config.color,
                      }}
                      title={`${config.label} - ${format(new Date(assessment.assessedAt), 'dd/MM/yyyy')}`}
                    />

                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden rounded bg-gray-900 px-2 py-1 text-xs text-white group-hover:block">
                      <div className="font-medium">{config.label}</div>
                      <div>{format(new Date(assessment.assessedAt), 'dd/MM/yyyy')}</div>
                      <div>Score: {assessment.clinicalScore}%</div>
                    </div>

                    {/* X-axis label */}
                    <span className="mt-2 text-[10px] text-muted-foreground">
                      {format(new Date(assessment.assessedAt), 'dd/MM', { locale: ptBR })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-4">
        {Object.entries(RiskLevelConfig).map(([level, config]) => (
          <div key={level} className="flex items-center gap-2">
            <div
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: config.color }}
            />
            <span className="text-xs text-muted-foreground">{config.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
